import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FiAlertCircle, FiCalendar, FiCheck, FiCheckCircle, FiEdit, FiEye, FiEyeOff, FiHash, FiLock, FiLogOut, FiMail, FiPhone, FiShield, FiUser, FiX} from 'react-icons/fi';
import {useLocation, useNavigate} from 'react-router-dom';
import {accountsApi, authStorage, getAccessCapabilities, getErrorFields, getErrorMessage} from '../services/api';
import './ProfilePage.css';

const emptyProfile = {email: '', first_name: '', last_name: '', student_number: '', phone_number: '', date_joined: ''};
const emptyPasswords = {old_password: '', new_password: '', confirm_password: ''};
const editableFields = ['first_name', 'last_name', 'email'];

const getFieldErrors = (error, allowedFields) => {
    const fields = getErrorFields(error);
    return allowedFields.reduce((result, field) => {
        const errorText = fields[field]?.[0];
        if (errorText) result[field] = errorText;
        return result;
    }, {});
};

const passwordChecks = (password) => [
    {label: 'حداقل ۸ نویسه', passed: password.length >= 8},
    {label: 'حروف کوچک و بزرگ انگلیسی', passed: /[a-z]/.test(password) && /[A-Z]/.test(password)},
    {label: 'حداقل یک عدد', passed: /\d/.test(password)},
    {label: 'حداقل یک نماد', passed: /[^\p{L}\p{N}\s]/u.test(password)},
];

function PasswordInput({id, label, value, onChange, error, onCapsLockChange}) {
    const [visible, setVisible] = useState(false);
    return <div className="profile-form-group">
        <label htmlFor={id}>{label}</label>
        <div className={`password-input-wrap ${error ? 'has-error' : ''}`}>
            <input id={id} required type={visible ? 'text' : 'password'} className="academic-input" value={value} onChange={onChange}
                onKeyUp={(event) => onCapsLockChange(event.getModifierState?.('CapsLock'))}
                onKeyDown={(event) => onCapsLockChange(event.getModifierState?.('CapsLock'))}
                autoComplete={id === 'current-password' ? 'current-password' : 'new-password'} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined}/>
            <button type="button" className="password-visibility" onClick={() => setVisible((current) => !current)} aria-label={visible ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}>
                {visible ? <FiEyeOff/> : <FiEye/>}
            </button>
        </div>
        {error && <small id={`${id}-error`} className="field-error">{error}</small>}
    </div>;
}

export default function ProfilePage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(emptyProfile);
    const [draft, setDraft] = useState(emptyProfile);
    const [passwords, setPasswords] = useState(emptyPasswords);
    const [editMode, setEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [capsLockOn, setCapsLockOn] = useState(false);

    const requestedTab = useMemo(() => new URLSearchParams(location.search).get('tab') || 'profile', [location.search]);
    const activeTab = requestedTab === 'security' ? 'security' : 'profile';
    const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'کاربر سامانه';
    const isDirty = editMode && editableFields.some((field) => (draft[field] || '') !== (profile[field] || ''));
    const checks = useMemo(() => passwordChecks(passwords.new_password), [passwords.new_password]);
    const strength = checks.filter((check) => check.passed).length;
    const capabilities = useMemo(() => getAccessCapabilities(profile), [profile]);
    const roleLabels = useMemo(() => {
        const labels = [];
        if (capabilities.isPlatformAdmin) labels.push('مدیر سامانه');
        if (capabilities.isOrganizationManager) labels.push('مدیر سازمان');
        if (profile.is_teacher) labels.push('استاد');
        if (profile.is_student) labels.push('دانشجو');
        return labels.length ? labels : ['بدون نقش سازمانی'];
    }, [capabilities, profile.is_student, profile.is_teacher]);

    const loadProfile = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await accountsApi.profile();
            setProfile({...emptyProfile, ...data});
            setDraft({...emptyProfile, ...data});
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
            if (error.status === 401) navigate('/login', {replace: true});
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    useEffect(() => { loadProfile(); }, [loadProfile]);
    useEffect(() => {
        if (requestedTab !== activeTab) navigate(`/profilepage?tab=${activeTab}`, {replace: true});
    }, [activeTab, navigate, requestedTab]);
    useEffect(() => {
        const warnBeforeUnload = (event) => {
            if (!isDirty) return;
            event.preventDefault();
            event.returnValue = '';
        };
        const warnLinkNavigation = (event) => {
            if (!isDirty || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            const link = event.target.closest?.('a[href]');
            if (!link || link.href === window.location.href || window.confirm('تغییرات ذخیره‌نشده دارید. بدون ذخیره از این صفحه خارج می‌شوید؟')) return;
            event.preventDefault();
            event.stopPropagation();
        };
        window.addEventListener('beforeunload', warnBeforeUnload);
        document.addEventListener('click', warnLinkNavigation, true);
        return () => {
            window.removeEventListener('beforeunload', warnBeforeUnload);
            document.removeEventListener('click', warnLinkNavigation, true);
        };
    }, [isDirty]);

    const updateDraft = (field, value) => {
        setDraft((current) => ({...current, [field]: value}));
        setFieldErrors((current) => ({...current, [field]: ''}));
    };

    const saveProfile = async () => {
        if (draft.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) {
            setFieldErrors({email: 'فرمت پست الکترونیکی معتبر نیست.'});
            return;
        }
        setIsSaving(true);
        setMessage(null);
        setFieldErrors({});
        try {
            const data = await accountsApi.updateProfile({email: draft.email.trim(), first_name: draft.first_name.trim(), last_name: draft.last_name.trim()});
            setProfile({...emptyProfile, ...data});
            setDraft({...emptyProfile, ...data});
            setEditMode(false);
            setMessage({type: 'success', text: 'اطلاعات پروفایل با موفقیت ذخیره شد.'});
        } catch (error) {
            setFieldErrors(getFieldErrors(error, editableFields));
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsSaving(false);
        }
    };

    const changePassword = async (event) => {
        event.preventDefault();
        setMessage(null);
        setFieldErrors({});
        const errors = {};
        if (passwords.new_password.length < 8) errors.new_password = 'رمز عبور باید حداقل ۸ نویسه داشته باشد.';
        if (passwords.new_password === passwords.old_password) errors.new_password = 'رمز عبور جدید باید با رمز فعلی متفاوت باشد.';
        if (passwords.new_password !== passwords.confirm_password) errors.confirm_password = 'تکرار رمز عبور با رمز جدید یکسان نیست.';
        if (Object.keys(errors).length) {
            setFieldErrors(errors);
            return;
        }
        setIsSaving(true);
        try {
            await accountsApi.changePassword({old_password: passwords.old_password, new_password: passwords.new_password});
            setPasswords(emptyPasswords);
            navigate('/login', {replace: true});
        } catch (error) {
            setFieldErrors(getFieldErrors(error, ['old_password', 'new_password']));
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsSaving(false);
        }
    };

    const cancelEdit = () => {
        setDraft(profile);
        setFieldErrors({});
        setEditMode(false);
        setMessage(null);
    };

    const logout = async () => {
        if (isDirty && !window.confirm('تغییرات ذخیره‌نشده دارید. از حساب خارج می‌شوید؟')) return;
        setIsSaving(true);
        try {
            await accountsApi.logout();
        } finally {
            navigate('/', {replace: true});
        }
    };

    if (isLoading) return <div className="academic-profile" aria-busy="true"><main className="profile-container profile-skeleton">
        <div className="profile-content"><span/><span/><span/><span/></div><aside className="profile-sidebar"><div className="user-card"><span/><span/><span/></div></aside>
    </main></div>;

    return <div className="academic-profile"><main className="profile-container">
        <div className="profile-content">
            {message && <div className={`api-message ${message.type}`} role="status">{message.type === 'error' ? <FiAlertCircle/> : <FiCheckCircle/>}<span>{message.text}</span></div>}
            {activeTab === 'profile' && <section className="profile-section">
                <div className="section-header app-page-header app-page-header--plain"><div><h1><FiUser className="section-icon"/> اطلاعات کاربری</h1><p>مشخصات هویتی و راه‌های شناسایی حساب شما</p></div>
                    {!editMode ? <button type="button" className="edit-button" onClick={() => { setEditMode(true); setMessage(null); }}><FiEdit/> ویرایش</button> : <div className="edit-actions">
                        <button type="button" className="save-button" disabled={isSaving || !isDirty} onClick={saveProfile}><FiCheck/> {isSaving ? 'در حال ذخیره…' : 'ذخیره'}</button>
                        <button type="button" className="cancel-button" disabled={isSaving} onClick={cancelEdit}><FiX/> انصراف</button>
                    </div>}
                </div>
                {isDirty && <div className="unsaved-note"><FiAlertCircle/> تغییرات هنوز ذخیره نشده‌اند.</div>}
                <div className="profile-form">
                    <div className="form-row">
                        <div className="profile-form-group"><label htmlFor="profile-first-name">نام</label>{editMode ? <><input id="profile-first-name" className={`academic-input ${fieldErrors.first_name ? 'invalid' : ''}`} value={draft.first_name} onChange={(event) => updateDraft('first_name', event.target.value)} aria-invalid={Boolean(fieldErrors.first_name)}/>{fieldErrors.first_name && <small className="field-error">{fieldErrors.first_name}</small>}</> : <div className="profile-field">{profile.first_name || 'ثبت نشده'}</div>}</div>
                        <div className="profile-form-group"><label htmlFor="profile-last-name">نام خانوادگی</label>{editMode ? <><input id="profile-last-name" className={`academic-input ${fieldErrors.last_name ? 'invalid' : ''}`} value={draft.last_name} onChange={(event) => updateDraft('last_name', event.target.value)} aria-invalid={Boolean(fieldErrors.last_name)}/>{fieldErrors.last_name && <small className="field-error">{fieldErrors.last_name}</small>}</> : <div className="profile-field">{profile.last_name || 'ثبت نشده'}</div>}</div>
                    </div>
                    <div className="profile-form-group"><label htmlFor="profile-email">پست الکترونیکی</label>{editMode ? <><input id="profile-email" type="email" dir="ltr" className={`academic-input ${fieldErrors.email ? 'invalid' : ''}`} value={draft.email} onChange={(event) => updateDraft('email', event.target.value)} aria-invalid={Boolean(fieldErrors.email)}/>{fieldErrors.email && <small className="field-error">{fieldErrors.email}</small>}</> : <div className="profile-field profile-identifier" dir="ltr">{profile.email || 'ثبت نشده'}</div>}</div>
                    <div className="form-row readonly-identifiers">
                        <div className="profile-form-group"><label>شماره دانشجویی</label><div className="profile-field profile-identifier" dir="ltr">{profile.student_number || 'ثبت نشده'}</div><small>این شناسه توسط مدیر مجموعه تنظیم می‌شود.</small></div>
                        <div className="profile-form-group"><label>شماره موبایل</label><div className="profile-field profile-identifier" dir="ltr">{profile.phone_number || 'ثبت نشده'}</div><small>برای تغییر این شناسه با مدیر مجموعه تماس بگیرید.</small></div>
                    </div>
                </div>
            </section>}
            {activeTab === 'security' && <section className="profile-section">
                <div className="section-header app-page-header app-page-header--plain"><div><h1><FiLock className="section-icon"/> امنیت حساب</h1><p>یک رمز عبور تازه و غیرتکراری برای حساب خود انتخاب کنید.</p></div></div>
                <div className="security-layout"><form className="security-form" onSubmit={changePassword}>
                    <PasswordInput id="current-password" label="رمز عبور فعلی" value={passwords.old_password} error={fieldErrors.old_password} onCapsLockChange={setCapsLockOn} onChange={(event) => { setPasswords({...passwords, old_password: event.target.value}); setFieldErrors({...fieldErrors, old_password: ''}); }}/>
                    <PasswordInput id="new-password" label="رمز عبور جدید" value={passwords.new_password} error={fieldErrors.new_password} onCapsLockChange={setCapsLockOn} onChange={(event) => { setPasswords({...passwords, new_password: event.target.value}); setFieldErrors({...fieldErrors, new_password: ''}); }}/>
                    <PasswordInput id="confirm-password" label="تکرار رمز عبور جدید" value={passwords.confirm_password} error={fieldErrors.confirm_password} onCapsLockChange={setCapsLockOn} onChange={(event) => { setPasswords({...passwords, confirm_password: event.target.value}); setFieldErrors({...fieldErrors, confirm_password: ''}); }}/>
                    {capsLockOn && <div className="caps-warning"><FiAlertCircle/> Caps Lock روشن است.</div>}
                    <div className="form-actions"><button className="save-button" disabled={isSaving}><FiShield/> {isSaving ? 'در حال تغییر…' : 'تغییر رمز عبور'}</button></div>
                </form><aside className="password-guide"><h2>راهنمای رمز امن</h2><div className={`strength-meter strength-${strength}`} aria-label={`قدرت رمز ${strength} از ۴`}><i/><i/><i/><i/></div>
                    <ul>{checks.map((check) => <li className={check.passed ? 'passed' : ''} key={check.label}><FiCheckCircle/>{check.label}</li>)}</ul><p>پس از تغییر رمز، برای محافظت از حساب باید دوباره وارد شوید.</p>
                </aside></div>
            </section>}
        </div>
        <aside className="profile-sidebar"><div className="user-card">
            <div className="identity-row"><div className="academic-avatar"><FiUser/></div><div className="user-info"><h2>{fullName}</h2><p>{profile.email || profile.student_number || profile.phone_number || 'شناسه ثبت نشده'}</p></div></div>
            <div className="role-badges">{roleLabels.map((label) => <span key={label}>{label}</span>)}</div>
            <div className="user-meta">{profile.email && <div className="meta-item"><FiMail/><span dir="ltr">{profile.email}</span></div>}{profile.student_number && <div className="meta-item"><FiHash/><span dir="ltr">{profile.student_number}</span></div>}{profile.phone_number && <div className="meta-item"><FiPhone/><span dir="ltr">{profile.phone_number}</span></div>}<div className="meta-item"><FiCalendar/><span>عضویت: {profile.date_joined ? new Date(profile.date_joined).toLocaleDateString('fa-IR') : 'نامشخص'}</span></div></div>
            <button className="logout-button" disabled={isSaving || !authStorage.isAuthenticated()} onClick={logout}><FiLogOut/> خروج از حساب</button>
        </div></aside>
    </main></div>;
}
